import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';

import type {
  GetPropertyImagesQuery,
  InsertPropertyImagesBatchMutation,
} from '@graphql/__generated__/graphql';
import {
  EditableImagesContext,
  type EditableImagesContextValue,
  type EditableImagesProviderProps,
  type ViewMode,
  getOptimisticReorderResponse,
} from '@modules/_shared/components/image-gallery/context/editableImagesContext';
import { useImageMetadataGeneration } from '@modules/_shared/components/image-gallery/context/useImageMetadataGeneration';
import {
  PROPERTY_IMAGE_VERSION_CONFLICT,
  type SaveListingImageVersionInput,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-types';
import {
  base64PngToJpegFile,
  useEnhanceImage,
} from '@modules/_shared/components/image-gallery/enhance/enhance-image-utils';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useCompositeWatermarkImages } from '@modules/_shared/components/watermark/useCompositeWatermarkImages';
import {
  uploadFileAsync,
  useFileUpload,
} from '@modules/_shared/utils/files/file-upload';
import {
  ACTIVATE_PROPERTY_IMAGE_VERSION,
  DELETE_PROPERTY_IMAGE,
  GENERATE_IMAGE_METADATA,
  GET_PROPERTY_IMAGES,
  INSERT_PROPERTY_IMAGES_BATCH,
  REORDER_PROPERTY_IMAGES,
  SAVE_PROPERTY_IMAGE_VERSION,
  UPDATE_PROPERTY_IMAGE,
} from '@modules/listings/ListingPropertyImages/listingPropertyQueries';
import { updatePropertyImagesCountCache } from '@modules/listings/ListingPropertyImages/propertyImageCache';
import { findWatermarkLotForRegeneration } from '@modules/listings/ListingPropertyImages/propertyImageWatermark';
import type {
  PropertyImage,
  PropertyImageForm,
} from '@modules/listings/ListingPropertyImages/types';
import { useLocale } from '@shared/hooks/locale';

export const ListingPropertyImagesProvider = ({
  children,
  propertyId,
  lotId,
  metaColumns = [],
  canEditMeta = false,
  autoSave = false,
  enableViewMode = true,
  autoSelectFirst = false,
  onUpdate,
  onAdd,
  readOnly = false,
  initialViewMode = 'list',
}: EditableImagesProviderProps<PropertyImage> & {
  propertyId: string;
  lotId?: string | null;
  readOnly?: boolean;
  onUpdate?: () => void;
  onAdd?: (
    newImages: NonNullable<
      InsertPropertyImagesBatchMutation['insert_property_images']
    >['returning'],
  ) => void;
}) => {
  const { t, language } = useLocale();

  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [metadataError, setMetadataError] = useState<ErrorLike | null>(null);

  const hideSnackbar = useCallback(() => {
    setSnackbarMessage(null);
  }, []);

  const apolloClient = useApolloClient();

  const { data, loading, refetch } = useQuery(GET_PROPERTY_IMAGES, {
    variables: { property_id: propertyId },
    skip: propertyId == null,
    fetchPolicy: 'network-only',
  });
  const [optimisticImages, setOptimisticImages] = useState<
    PropertyImage[] | null
  >(null);

  const [deleteImageMutation] = useMutation(DELETE_PROPERTY_IMAGE, {
    onError: () => {
      setSnackbarMessage(t('An error occured while deleting the image.'));
    },
    update: (cache, { data: deletedData }, { variables }) => {
      if (!variables || !deletedData?.delete_property_images_by_pk) {
        return;
      }

      cache.evict({ id: `property_images:${variables.id}` });
      updatePropertyImagesCountCache(cache, propertyId, -1);
    },
  });

  const [insertImagesBatchMutation] = useMutation(
    INSERT_PROPERTY_IMAGES_BATCH,
    {
      onError: () => {
        setSnackbarMessage(t('The images could not be uploaded.'));
      },
      update: (cache, { data }) => {
        const newImages = data?.insert_property_images?.returning ?? [];
        if (newImages.length === 0 || !propertyId) {
          return;
        }

        const existing = cache.readQuery<GetPropertyImagesQuery>({
          query: GET_PROPERTY_IMAGES,
          variables: { property_id: propertyId },
        });

        cache.writeQuery({
          query: GET_PROPERTY_IMAGES,
          variables: { property_id: propertyId },
          data: {
            watermark_lots: existing?.watermark_lots ?? [],
            property_images: [
              ...(existing?.property_images ?? []),
              ...newImages,
            ],
          },
        });
        updatePropertyImagesCountCache(cache, propertyId, newImages.length);
      },
    },
  );

  const [reorderImageMutation] = useMutation(REORDER_PROPERTY_IMAGES, {
    onError: () => {
      setSnackbarMessage(t('An error occured while reordering the images.'));
    },
    update: (cache, _res, { variables }) => {
      if (!variables?.id || !propertyId) {
        return;
      }

      const cacheData = cache.readQuery<GetPropertyImagesQuery>({
        query: GET_PROPERTY_IMAGES,
        variables: { property_id: propertyId },
      });

      if (!cacheData) {
        return;
      }

      const targetIndex = cacheData.property_images.findIndex(
        img => img.order_nr === variables.new_order && img.id !== variables.id,
      );

      const reorderedImages = getOptimisticReorderResponse(
        cacheData.property_images,
        variables.id,
        targetIndex,
      );

      if (reorderedImages == null) {
        return;
      }

      cache.writeQuery({
        query: GET_PROPERTY_IMAGES,
        variables: { property_id: propertyId },
        data: {
          watermark_lots: cacheData.watermark_lots,
          property_images: reorderedImages,
        },
      });
    },
  });

  const [updateImageMutation] = useMutation(UPDATE_PROPERTY_IMAGE, {
    onError: () => {
      setSnackbarMessage(t('An error occured while updating the image.'));
    },
  });
  const [saveImageVersionMutation] = useMutation(SAVE_PROPERTY_IMAGE_VERSION, {
    fetchPolicy: 'no-cache',
  });
  const [activateImageVersionMutation] = useMutation(
    ACTIVATE_PROPERTY_IMAGE_VERSION,
    { fetchPolicy: 'no-cache' },
  );
  const {
    compositeImages,
    error: watermarkError,
    reset: resetWatermarkError,
    snackbar: skippedWatermarkWarning,
  } = useCompositeWatermarkImages();

  const enhanceImage = useEnhanceImage({ imageSize: '2K' });
  const [uploadFile] = useFileUpload('image');

  const regenerateWatermark = useCallback(
    async (previousWatermarkedUrl: string | null) => {
      try {
        const lots = data?.watermark_lots ?? [];
        const watermarkLot = findWatermarkLotForRegeneration(
          lots,
          lotId,
          previousWatermarkedUrl,
        );

        if (!watermarkLot) {
          if (previousWatermarkedUrl != null) {
            setSnackbarMessage(
              t('The watermark could not be regenerated for this photo.'),
            );
          }
          return false;
        }

        await compositeImages(watermarkLot.id, { awaitRefetchQueries: true });
        return true;
      } catch {
        // The mutation hook exposes the error in the snackbar below.
        return false;
      }
    },
    [compositeImages, data?.watermark_lots, lotId, t],
  );

  const runImageVersionMutation = useCallback(
    async (propertyImageId: string, mutate: () => Promise<boolean>) => {
      const previousWatermarkedUrl =
        data?.property_images.find(image => image.id === propertyImageId)
          ?.watermarked_url ?? null;
      try {
        if (!(await mutate())) {
          throw new Error(PROPERTY_IMAGE_VERSION_CONFLICT);
        }
        if (!(await regenerateWatermark(previousWatermarkedUrl))) {
          await refetch();
        }
        onUpdate?.();
      } catch (error) {
        try {
          await refetch();
        } catch {
          // Preserve the mutation error so concurrent-update conflicts stay actionable.
        }
        throw error;
      }
    },
    [data?.property_images, onUpdate, refetch, regenerateWatermark],
  );

  const deleteImage = useCallback(
    async (id: string) => {
      await deleteImageMutation({
        variables: { id },
        onCompleted: () => {
          onUpdate?.();
        },
      });
    },
    [deleteImageMutation, onUpdate],
  );

  const updateImage = useCallback(
    async (id: string, updateData: PropertyImageForm) => {
      const result = await updateImageMutation({
        variables: { id, object: updateData },
      });

      const succeeded = result.data?.update_property_images_by_pk != null;
      if (succeeded) {
        onUpdate?.();
      }
      return succeeded;
    },
    [updateImageMutation, onUpdate],
  );

  const fetchMetadata = useCallback(
    async (imageId: string) => {
      const result = await apolloClient.query({
        query: GENERATE_IMAGE_METADATA,
        variables: { property_image_id: imageId, language },
        fetchPolicy: 'network-only',
      });
      return result.data?.generate_image_metadata ?? null;
    },
    [apolloClient, language],
  );

  const updateImageMetadata = useCallback(
    async (
      imageId: string,
      metadata: { title: string | null; description: string | null },
    ) => {
      const result = await updateImageMutation({
        variables: { id: imageId, object: metadata },
      });
      if (result.data?.update_property_images_by_pk) {
        onUpdate?.();
      }
    },
    [updateImageMutation, onUpdate],
  );

  const {
    loadingMetadataIds,
    regeneratingAll,
    generateMetadataForImage,
    regenerateAllMetadata,
  } = useImageMetadataGeneration({
    fetchMetadata,
    updateImage: updateImageMetadata,
    images: data?.property_images ?? [],
    onComplete: onUpdate,
    onError: error => setMetadataError(error as ErrorLike),
  });

  const insertImageBatch = useCallback(
    async (
      uploadedFiles: { id: string; url: string }[],
      aiEnhancedAt?: string,
    ) => {
      const result = await insertImagesBatchMutation({
        variables: {
          objects: uploadedFiles.map(file => ({
            property_id: propertyId,
            image: {
              data: {
                url: file.url,
                id: file.id,
                ...(aiEnhancedAt && { ai_enhanced_at: aiEnhancedAt }),
              },
            },
          })),
        },
      });

      const newImages = result.data?.insert_property_images?.returning ?? [];
      onAdd?.(newImages);
      onUpdate?.();
      return newImages;
    },
    [insertImagesBatchMutation, propertyId, onAdd, onUpdate],
  );

  const saveImageVersion = useCallback(
    async (propertyImageId: string, input: SaveListingImageVersionInput) => {
      const file = await base64PngToJpegFile(
        input.imageData,
        'ai-enhanced.jpg',
      );
      const uploadedFile = await uploadFileAsync(uploadFile, file);

      await runImageVersionMutation(propertyImageId, async () => {
        const result = await saveImageVersionMutation({
          variables: {
            propertyImageId,
            newImageId: uploadedFile.id,
            newImageUrl: uploadedFile.url,
            sourceImageId: input.sourceImageId,
            prompt: input.prompt,
            expectedCurrentImageId: input.expectedCurrentImageId,
          },
        });
        return result.data?.save_property_image_version[0] != null;
      });
    },
    [runImageVersionMutation, saveImageVersionMutation, uploadFile],
  );

  const activateImageVersion = useCallback(
    async (
      propertyImageId: string,
      imageId: string,
      expectedCurrentImageId: string,
    ) => {
      await runImageVersionMutation(propertyImageId, async () => {
        const result = await activateImageVersionMutation({
          variables: {
            propertyImageId,
            targetImageId: imageId,
            expectedCurrentImageId,
          },
        });
        return result.data?.activate_property_image_version[0] != null;
      });
    },
    [activateImageVersionMutation, runImageVersionMutation],
  );

  const reorderImage = useCallback(
    async (id: string, targetPosition: number) => {
      if (!data?.property_images) {
        return;
      }

      const images = [...data.property_images];
      const draggedIndex = images.findIndex(img => img.id === id);
      if (draggedIndex === -1) {
        return;
      }

      const safeTargetIndex = Math.max(0, targetPosition - 1);
      const targetOrder = images[safeTargetIndex].order_nr;

      const reorderedImages = getOptimisticReorderResponse(
        images,
        id,
        safeTargetIndex,
      );

      setOptimisticImages(reorderedImages);

      try {
        await reorderImageMutation({
          variables: { id, new_order: targetOrder },
        });
        onUpdate?.();
      } finally {
        setOptimisticImages(null);
      }
    },
    [reorderImageMutation, onUpdate, data],
  );

  const value = useMemo<
    EditableImagesContextValue<PropertyImage, PropertyImageForm>
  >(
    () => ({
      readOnly,
      canEditMeta,
      autoSave,
      enableViewMode,
      autoSelectFirst,
      metaColumns,
      images: optimisticImages ?? data?.property_images ?? [],
      loading,
      loadingMetadataIds,
      deleteImage,
      updateImage,
      uploadImages: async uploadedFiles => {
        await insertImageBatch(uploadedFiles);
      },
      reorderImage,
      generateMetadataForImage,
      regenerateAllMetadata,
      regeneratingAll,
      enhanceImage,
      saveImageVersion,
      activateImageVersion,
      viewMode,
      setViewMode,
      snackbarMessage,
      hideSnackbar,
    }),
    [
      readOnly,
      canEditMeta,
      autoSave,
      enableViewMode,
      autoSelectFirst,
      metaColumns,
      data?.property_images,
      loading,
      loadingMetadataIds,
      optimisticImages,
      deleteImage,
      updateImage,
      insertImageBatch,
      reorderImage,
      generateMetadataForImage,
      regenerateAllMetadata,
      regeneratingAll,
      enhanceImage,
      saveImageVersion,
      activateImageVersion,
      viewMode,
      snackbarMessage,
      hideSnackbar,
    ],
  );

  return (
    <EditableImagesContext.Provider value={value}>
      {children}
      <MutationErrorSnackbar
        error={metadataError}
        onClose={() => setMetadataError(null)}
      />
      <MutationErrorSnackbar
        error={watermarkError ?? null}
        onClose={resetWatermarkError}
      />
      {skippedWatermarkWarning}
    </EditableImagesContext.Provider>
  );
};
